import { parseJwt, RequestHandler } from '../../components/utils/utils'
import { EVENT, EVENTS } from '../../components/utils/eventBus'
import { HYPERSIGN_EXPLORER_BASE_URL, HYPERSIGN_EXPLORER_SSI_API_DID_PATH } from '../../config'
import LANG from '../../components/utils/lang/en'

const isJwtExpiredMessage = (message) => {
  if (typeof message !== 'string') return false

  const normalizedMessage = message.toLowerCase()
  return /\bjwt expired\b/.test(normalizedMessage) || /\bsession expired\b/.test(normalizedMessage)
}

export default {
  authenticateKYC: ({ state, getters, commit }, payload) => {
    return new Promise(async (resolve, reject) => {
      try {
        const url = `${getters.getTenantKycServiceBaseUrl}/e-kyc/verification/auth`
        const headers = {
          Authorization: 'Bearer ' + getters.getAuthServerAuthToken,
          'x-kyc-access-token': getters.getCavachAccessToken,
          'x-ssi-access-token': getters.getSSIAccessToken,
        }

        const body = {
          provider: 'hypersign-auth-service',
          sessionId: getters.getSession,
        }
        const json = await RequestHandler(url, 'POST', body, headers)

        if (json) {
          if (json.kycServiceUserAccessToken) {
            commit('setKycServiceUserAccessToken', json.kycServiceUserAccessToken)
            return resolve()
          } else {
            const errorMsg = typeof json === 'string' ? json : LANG.AUTH.KYC_TOKEN_MISSING
            if (isJwtExpiredMessage(errorMsg)) {
              commit('SET_STEP', 'SessionExpired')
            }
            console.error('authenticateKYC:: Error:', errorMsg)
            return reject(new Error(`${LANG.AUTH.KYC_AUTH_FAILED}: ${errorMsg}`))
          }
        } else {
          console.error('authenticateKYC:: No response received')
          return reject(new Error(`${LANG.AUTH.KYC_AUTH_FAILED}: ${LANG.AUTH.KYC_NO_RESPONSE}`))
        }
      } catch (e) {
        console.error('authenticateKYC:: Exception caught:', e)
        const errorMessage = e.message || LANG.AUTH.AUTH_ERROR
        if (isJwtExpiredMessage(errorMessage)) {
          commit('SET_STEP', 'SessionExpired')
        }
        return reject(new Error(`${LANG.AUTH.KYC_AUTH_FAILED}: ${errorMessage}`))
      }
    })
  },

  // TODO: Change name of this method to somethin liek, submitUserConsent()
  verifyResult: ({ commit, getters, state }) => {
    return new Promise(async (resolve, reject) => {
      // eslint-disable-line
      if (Object.keys(state.userPresentationConsent).length <= 0) {
        return reject(new Error(LANG.USER_CONSENT.NO_CONSENT))
      }

      try {
        const url = `${getters.getTenantKycServiceBaseUrl}/e-kyc/verification/user-consent`
        const headers = {
          Authorization: 'Bearer ' + getters.getKycServiceUserAccessToken,
          'x-kyc-access-token': getters.getCavachAccessToken,
        }
        if (getters.getWidgetConfigFromDb?.isWidgetLogin !== false && getters.getAuthServerAuthToken) {
          headers['x-authserver-access-token'] = getters.getAuthServerAuthToken
        }
        const body = {
          sessionId: getters.getSession,
          presentation: state.userPresentationConsent,
        }
        const json = await RequestHandler(url, 'POST', body, headers)
        if (json && json.success === true) {
          commit('setResult', json.success)
          commit('setIdToken', json.idToken)
          resolve(json)
        } else {
          return reject(new Error(json))
        }
      } catch (e) {
        reject(new Error(`${LANG.USER_CONSENT.VERIFICATION_FAILED}: ${e}`))
      }
    })
  },

  verifyLiveliness: ({ commit, state, getters, dispatch }) => {
    return new Promise(async (resolve, reject) => {
      // eslint-disable-line
      if (state.livelinessCapturedData.tokenSelfiImage === '') {
        return reject(new Error(LANG.LIVELINESS.VERIFICATION_FAILED))
      }
      try {
        if (!getters.getSession) {
          await dispatch('getNewSession')
        }
        const sessionId = getters.getSession
        if (!sessionId) {
          throw new Error('Session ID is missing')
        }

        const url = `${getters.getTenantKycServiceBaseUrl}/e-kyc/verification/passive-liveliness`
        const headers = {
          Authorization: 'Bearer ' + getters.getKycServiceUserAccessToken,
          'x-kyc-access-token': getters.getCavachAccessToken,
          'x-ssi-access-token': getters.getSSIAccessToken,
          'x-issuer-did': getters.getPresentationRequestParsed.issuerDID,
          'x-issuer-did-ver-method': getters.getPresentationRequestParsed.issuerDIDVerificationMethod,
        }
        if (getters.getWidgetConfigFromDb?.isWidgetLogin !== false && getters.getAuthServerAuthToken) {
          headers['x-authserver-access-token'] = getters.getAuthServerAuthToken
        }
        const body = {
          sessionId,
          tokenSelfiImage: state.livelinessCapturedData.tokenSelfiImage,
          biometricTemplateRaw: state.livelinessCapturedData.biometricTemplateRaw,
          bestImageTokenized: state.livelinessCapturedData.bestImageTokenized,
          userDID: getters.getUserDID,
        }
        const json = await RequestHandler(url, 'POST', body, headers)

        if (json && json.serviceLivenessResult === 3) {
          commit('setLivelinessResult', json)
          if (json.credential) {
            try {
              await dispatch('updateVaultCredentials', json.credential)
            } catch (credError) {
              console.error('Failed to update vault credentials after liveliness check:', credError)
              // Continue anyway as the liveliness was successful
            }
          }
          return resolve(json)
        } else {
          if (isJwtExpiredMessage(json)) {
            commit('SET_STEP', 'SessionExpired')
          }
          return reject(new Error(json))
        }
      } catch (e) {
        if (isJwtExpiredMessage(e.message || e)) {
          commit('SET_STEP', 'SessionExpired')
        }
        reject(new Error(`${LANG.LIVELINESS.VERIFICATION_FAILED}: ${e}`))
      }
    })
  },

  // eventSource -----
  verifyLivelinessStatus({ getters }) {
    const sessionId = getters.getSession
    const url = `${getters.getTenantKycServiceBaseUrl}/e-kyc/verification/passive-liveliness/status/` + sessionId
    const eventSource = new EventSource(url + '?' + `x-kyc-access-token=${getters.getCavachAccessToken}`, {})
    eventSource.onopen = function () {}
    eventSource.onmessage = function (event) {
      // console.log('New message:', event.data);
      const payload = {
        success: true,
        message: event.data,
      }
      if (event.data.includes('not generated') || event.data.includes('completed')) {
        eventSource.close()
      }

      if (event.data.includes('error')) {
        payload.success = false
        eventSource.close()
      }
      EVENT.emitEvent(EVENTS.LIVELINESS, payload)
    }
    eventSource.onerror = function (error) {
      console.error('EventSource failed:', error)
      eventSource.close()
    }
  },
  // ---------------

  verifyOcrIDDoc: ({ commit, state, getters, dispatch }) => {
    return new Promise(async (resolve, reject) => {
      // eslint-disable-line
      if (state.kycCapturedData.tokenFrontDocumentImage === '' || !state.hasKycDone) {
        return reject(new Error(LANG.IDDOCUMENT.MISSING_ID_CAPTURE))
      }

      if (!state.livelinessCapturedData.bestImageTokenized) {
        return reject(new Error(LANG.IDDOCUMENT.MISSING_FACIAL_RECOGNITION))
      }
      try {
        const url = `${getters.getTenantKycServiceBaseUrl}/e-kyc/verification/doc-ocr`
        const headers = {
          Authorization: 'Bearer ' + getters.getKycServiceUserAccessToken,
          'x-kyc-access-token': getters.getCavachAccessToken,
          'x-ssi-access-token': getters.getSSIAccessToken,
          'x-issuer-did': getters.getPresentationRequestParsed.issuerDID,
          'x-issuer-did-ver-method': getters.getPresentationRequestParsed.issuerDIDVerificationMethod,
        }
        if (getters.getWidgetConfigFromDb?.isWidgetLogin !== false && getters.getAuthServerAuthToken) {
          headers['x-authserver-access-token'] = getters.getAuthServerAuthToken
        }

        const body = {
          documentType: 0,
          documentIdType: state.kycCapturedData.documentIdType,
          // tokenFrontDocumentImage: state.kycCapturedData.tokenFrontDocumentImage,
          // tokenBackDocumentImage: state.kycCapturedData.tokenBackDocumentImage,
          bestImageTokenized: state.livelinessCapturedData.bestImageTokenized,
          tokenFaceImage: state.livelinessCapturedData.tokenSelfiImage || state.livelinessCapturedData.base64Image,
          countryCode: state.kycCapturedData.countryCode || 'XXX',
          sessionId: getters.getSession,
          userDID: getters.getUserDID,
          ocr: {},
          extractionToken: getters.getExtractionToken,
        }
        const json = await RequestHandler(url, 'POST', body, headers)
        if (json && json.serviceFacialAuthenticationResult === 3) {
          commit('setOcrIdDocResult', json)
          if (json.credentials && json.credentials.length > 0) {
            for (const credential of json.credentials) {
              try {
                await dispatch('updateVaultCredentials', credential)
              } catch (credError) {
                console.error('Failed to update vault credential ' + credential.id + ':', credError)
                // Continue with next credential
              }
            }
          }
          return resolve(json)
        } else {
          return reject(new Error(json))
        }
      } catch (e) {
        return reject(new Error(`${LANG.IDDOCUMENT.VERIFICATION_FAILED}: ${e.message}`))
      }
    })
  },

  extractOcrIdDoc: ({ state, getters, commit }, payload) => {
    return new Promise(async (resolve, reject) => {
      try {
        const url = `${getters.getTenantKycServiceBaseUrl}/e-kyc/verification/doc-ocr/extract`
        const headers = {
          Authorization: 'Bearer ' + getters.getKycServiceUserAccessToken,
          'x-kyc-access-token': getters.getCavachAccessToken,
          'x-ssi-access-token': getters.getSSIAccessToken,
          'x-issuer-did': getters.getPresentationRequestParsed.issuerDID,
          'x-issuer-did-ver-method': getters.getPresentationRequestParsed.issuerDIDVerificationMethod,
        }
        if (getters.getWidgetConfigFromDb?.isWidgetLogin !== false && getters.getAuthServerAuthToken) {
          headers['x-authserver-access-token'] = getters.getAuthServerAuthToken
        }
        const body = {
          tokenFrontDocumentImage: state.kycCapturedData.tokenFrontDocumentImage,
          tokenBackDocumentImage: state.kycCapturedData.tokenBackDocumentImage,
          sessionId: getters.getSession,
          documentType: payload.documentType,
          countryCode: payload.countryCode,
        }
        const json = await RequestHandler(url, 'POST', body, headers)
        if (json && json.extractionToken) {
          const data = parseJwt(json.extractionToken)
          commit('setExtractionToken', json.extractionToken)

          //  store the result to state
          return resolve(data)
        } else {
          return reject(new Error(json))
        }
      } catch (e) {
        return reject(new Error(`${LANG.IDDOCUMENT.EXTRACTION_FAILED}: ${e.message}`))
      }
    })
  },
  // eventSource -----
  verifyOCRDocStatus({ getters }) {
    const sessionId = getters.getSession
    const url = `${getters.getTenantKycServiceBaseUrl}/e-kyc/verification/doc-ocr/status/` + sessionId
    const eventSource = new EventSource(url + '?' + `x-kyc-access-token=${getters.getCavachAccessToken}`, {})
    eventSource.onopen = function () {}
    eventSource.onmessage = function (event) {
      // console.log('New message:', event.data);
      const payload = {
        success: true,
        message: event.data,
      }
      if (event.data.includes('not generated') || event.data.includes('completed')) {
        eventSource.close()
      }

      if (event.data.includes('error')) {
        payload.success = false
        eventSource.close()
      }
      EVENT.emitEvent(EVENTS.IDDOCOCR, payload)
    }
    eventSource.onerror = function (error) {
      console.error('EventSource failed:', error)
      eventSource.close()
    }
  },
  // ---------------

  verifySbtMint: ({ commit, getters, dispatch }, payload) => {
    return new Promise(async (resolve, reject) => {
      // eslint-disable-line
      try {
        const url = `${getters.getTenantKycServiceBaseUrl}/e-kyc/verification/sbt-mint`
        const headers = {
          Authorization: 'Bearer ' + getters.getKycServiceUserAccessToken,
          'x-kyc-access-token': getters.getCavachAccessToken,
          'x-ssi-access-token': getters.getSSIAccessToken,
          'x-issuer-did': getters.getPresentationRequestParsed.issuerDID,
          'x-issuer-did-ver-method': getters.getPresentationRequestParsed.issuerDIDVerificationMethod,
        }
        if (getters.getWidgetConfigFromDb?.isWidgetLogin !== false && getters.getAuthServerAuthToken) {
          headers['x-authserver-access-token'] = getters.getAuthServerAuthToken
        }
        const body = {
          sessionId: getters.getSession,
          userDID: getters.getUserDID,
          sbtData: {
            ...payload,
          },
        }

        const json = await RequestHandler(url, 'POST', body, headers)
        if (json.credentials && json.credentials.length > 0) {
          commit('setSbtMintDone', true)
          for (const credential of json.credentials) {
            try {
              await dispatch('updateVaultCredentials', credential)
            } catch (credError) {
              console.error('Failed to update vault credential ' + credential.id + ':', credError)
              // Continue with next credential
            }
          }
          return resolve(json)
        } else {
          return reject(new Error(json))
        }
      } catch (e) {
        reject(new Error(`${LANG.ON_CHAIN.SBT_MINT_FAILED}: ${e}`))
      }
    })
  },

  createAssetToMint: ({ getters }, payload) => {
    return new Promise(async (resolve, reject) => {
      // eslint-disable-line
      try {
        const url = `${getters.getTenantKycServiceBaseUrl}/e-kyc/verification/asset-metadata`
        const headers = {
          Authorization: 'Bearer ' + getters.getKycServiceUserAccessToken,
          'x-kyc-access-token': getters.getCavachAccessToken,
          'x-ssi-access-token': getters.getSSIAccessToken,
          'x-issuer-did': getters.getPresentationRequestParsed.issuerDID,
          'x-issuer-did-ver-method': getters.getPresentationRequestParsed.issuerDIDVerificationMethod,
        }
        if (getters.getWidgetConfigFromDb?.isWidgetLogin !== false && getters.getAuthServerAuthToken) {
          headers['x-authserver-access-token'] = getters.getAuthServerAuthToken
        }
        const body = {
          ...payload,
        }
        const json = await RequestHandler(url, 'POST', body, headers)
        if (json) {
          return resolve(json)
        } else {
          return reject(new Error(json))
        }
      } catch (e) {
        reject(new Error(`${LANG.ON_CHAIN.ASSET_CREATION_FAILED}: ${e}`))
      }
    })
  },

  verifyZkProof: ({ commit, getters, dispatch }, payload) => {
    // eslint-disable-line
    return new Promise(async (resolve, reject) => {
      // eslint-disable-line
      try {
        const url = `${getters.getTenantKycServiceBaseUrl}/e-kyc/verification/zk-proof`
        const headers = {
          Authorization: 'Bearer ' + getters.getKycServiceUserAccessToken,
          'x-kyc-access-token': getters.getCavachAccessToken,
          'x-ssi-access-token': getters.getSSIAccessToken,
          'x-issuer-did': getters.getPresentationRequestParsed.issuerDID,
          'x-issuer-did-ver-method': getters.getPresentationRequestParsed.issuerDIDVerificationMethod,
        }
        if (getters.getWidgetConfigFromDb?.isWidgetLogin !== false && getters.getAuthServerAuthToken) {
          headers['x-authserver-access-token'] = getters.getAuthServerAuthToken
        }

        const body = {
          sessionId: getters.getSession,
          userDID: getters.getUserDID,
          proof: payload.uncompressed_proof,
          publicSignals: payload.publicSignals,
          proofType: payload.proofType,
        }
        const json = await RequestHandler(url, 'POST', body, headers)

        if (json.credential && json.verifyResult) {
          // commit('setSbtMintDone', true);
          try {
            await dispatch('updateVaultCredentials', json.credential)
          } catch (credError) {
            console.error('Failed to update vault credential:', credError)
            // Continue anyway as the ZK proof was verified
          }
          return resolve(json)
        } else {
          if (isJwtExpiredMessage(json)) {
            commit('SET_STEP', 'SessionExpired')
          }
          return reject(new Error(json))
        }
      } catch (error) {
        if (isJwtExpiredMessage(error.message || error)) {
          commit('SET_STEP', 'SessionExpired')
        }
        reject(new Error(`${LANG.ZK_PROOF.VERIFICATION_FAILED}: ${error.message || error}`))
      }
      // resolve(json)
    })
  },
  resolveIssuerId: ({ commit, getters, dispatch }, payload) => {
    // eslint-disable-line
    return new Promise(async (resolve, reject) => {
      // eslint-disable-line
      try {
        // TODO: remove hardcoded URL
        const url = `${HYPERSIGN_EXPLORER_BASE_URL}${HYPERSIGN_EXPLORER_SSI_API_DID_PATH}/${payload.issuerDid}`

        const resp = await fetch(url)

        const json = await resp.json()
        if (resp.ok) {
          resolve(json)
        }

        // resolve(json)
      } catch (error) {
        reject(error)
      }
    })
  },
}
